/**
 * Admin panel: people, their authority, work oversight, and the audit trail.
 *
 * Controls render from the caller's OWN scopes (`/admin/me/scopes`) rather than
 * from their role name, so the interface never offers a button that would come
 * back 403. Scope keys and role defaults are fetched from the server, never
 * hardcoded here, so this page cannot drift from what is actually enforced.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { UserPlus, Timer,
  Shield, Users as UsersIcon, ListChecks, ScrollText, Search,
  KeyRound, UserCheck, UserX, AlertTriangle, Building2, Eye, Trash2,
} from 'lucide-react';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import {
  fetchMyScopes, fetchAdminOverview, fetchScopeCatalogue, fetchAdminUsers,
  setUserRole, setUserActive, setUserScopes, setUserAccess, deleteUser, fetchAdminTasks, reassignTask,
  fetchAudit, fetchClients, impersonateUser, fetchStageSla, saveStageSla, stepLabel, type StageSla, type AdminUser, type AdminTask,
} from '@/lib/queries';
import { startViewAs } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { SEEN_TRACKED_SINCE } from '@/lib/format';
import { cn } from '@/lib/cn';

const ROLES = ['owner', 'manager', 'agent', 'crew', 'freelancer', 'client'];

const ROLE_TONE: Record<string, string> = {
  owner: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  manager: 'bg-accent-400/15 text-accent-400 border-accent-400/30',
  agent: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  crew: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  freelancer: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  client: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

type Tab = 'people' | 'work' | 'scopes' | 'audit' | 'settings';

export default function AdminPanelPage() {
  const [tab, setTab] = useState<Tab>('people');
  const me = useQuery({ queryKey: ['my-scopes'], queryFn: fetchMyScopes });

  if (me.isLoading) return <div className="p-6 text-sm text-muted">Loading...</div>;

  // Fail closed in the UI too: without admin.panel we render the refusal rather
  // than an empty shell that 403s on every request.
  if (!me.data?.is_admin) {
    return (
      <div className="p-6">
        <Card className="max-w-md">
          <div className="flex items-start gap-3">
            <Shield size={18} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-sm font-medium text-muted-strong">Admin panel</h2>
              <p className="text-xs text-muted mt-1">
                Your account does not have the <code className="text-accent-400">admin.panel</code> permission.
                An owner can grant it.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const can = (s: string) => me.data.scopes.includes(s);
  const TABS: { id: Tab; label: string; icon: typeof UsersIcon; show: boolean }[] = [
    { id: 'people', label: 'People', icon: UsersIcon, show: can('users.read') },
    { id: 'work', label: 'Work', icon: ListChecks, show: can('tasks.read.all') },
    { id: 'scopes', label: 'Roles & permissions', icon: KeyRound, show: true },
    { id: 'audit', label: 'Audit', icon: ScrollText, show: can('audit.read') },
    { id: 'settings', label: 'Settings', icon: Timer, show: me.data.is_admin },
  ];

  return (
    <div className="p-6 max-w-6xl">
      <PageHeader title="Admin" subtitle="People, permissions and oversight" />
      <OverviewStrip />

      <div className="overflow-x-auto whitespace-nowrap flex gap-1 border-b border-slate-800 mb-5 mt-5 overflow-x-auto">
        {TABS.filter((t) => t.show).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition whitespace-nowrap',
              tab === t.id
                ? 'border-accent-400 text-accent-400'
                : 'border-transparent text-muted hover:text-muted-strong',
            )}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'people' && <PeopleTab can={can} />}
      {tab === 'work' && <WorkTab can={can} />}
      {tab === 'scopes' && <ScopesTab />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'settings' && <SettingsTab />}
    </div>
  );
}

function OverviewStrip() {
  const q = useQuery({ queryKey: ['admin-overview'], queryFn: fetchAdminOverview });
  const o = q.data;
  if (!o) return null;
  const tiles = [
    { label: 'Active people', value: o.users_active, sub: `${o.users_total} total` },
    { label: 'Clients', value: o.clients_total },
    { label: 'Creatives', value: o.creatives_total },
    { label: 'Open tasks', value: o.tasks_open, sub: o.tasks_blocked ? `${o.tasks_blocked} blocked` : undefined },
    { label: 'Never signed in', value: o.never_signed_in, warn: o.never_signed_in > 0,
      sub: o.no_signin_recorded ? `${o.no_signin_recorded} with no sign-in recorded` : undefined },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
      {tiles.map((t) => (
        <Card key={t.label} className="py-3">
          <div className="text-[10px] text-muted uppercase tracking-wider">{t.label}</div>
          <div className={cn('text-xl font-semibold tabular-nums mt-0.5',
            t.warn ? 'text-amber-400' : 'text-muted-strong')}>{t.value}</div>
          {t.sub && <div className="text-[11px] text-muted mt-0.5">{t.sub}</div>}
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

function PeopleTab({ can }: { can: (s: string) => boolean }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [access, setAccess] = useState<AdminUser | null>(null);
  const [confirm, setConfirm] = useState<{ u: AdminUser; next: boolean } | null>(null);
  const { user: meUser } = useAuth();
  // Read-only look through someone else's eyes. The server refuses writes on
  // this token, the banner says so, Exit restores the admin's own session.
  const viewAs = useMutation({
    mutationFn: (u: AdminUser) => impersonateUser(u.id),
    onSuccess: (r) => {
      startViewAs(r.access_token, { id: r.user.id, display_name: r.user.display_name, role: r.user.role,
        started_at: Date.now(), expires_at: Date.now() + r.expires_in * 1000 });
      window.location.href = '/';
    },
  });
  const clinics = useQuery({ queryKey: ['clients'], queryFn: fetchClients });
  const clinicName = (id: string) => clinics.data?.find((c) => c.id === id)?.name ?? id.slice(0, 8);

  const users = useQuery({
    queryKey: ['admin-users', q, role],
    queryFn: () => fetchAdminUsers({ q: q || undefined, role: role || undefined }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-users'] });
    qc.invalidateQueries({ queryKey: ['admin-overview'] });
  };

  const roleMut = useMutation({
    mutationFn: ({ id, r }: { id: string; r: string }) => setUserRole(id, r),
    onSuccess: (u) => { toast.success(`${u.display_name} is now ${u.role}`); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Could not change the role'),
  });
  const activeMut = useMutation({
    mutationFn: ({ id, a }: { id: string; a: boolean }) => setUserActive(id, a),
    onSuccess: (u) => { toast.success(`${u.display_name} ${u.active ? 'reactivated' : 'deactivated'}`); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Could not change status'),
  });
  // A refusal here explains what history holds the account, so show it long enough to read.
  const removeMut = useMutation({
    mutationFn: (u: AdminUser) => deleteUser(u.id),
    onSuccess: (r) => { toast.success(`${r.display_name} deleted`); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Could not delete the account', { duration: 12000 }),
  });
  const [removing, setRemoving] = useState<AdminUser | null>(null);

  return (
    <>
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email"
            className="w-full bg-bg-input border border-slate-700 rounded-lg pl-7 pr-2 py-1.5 text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400/50"
          />
        </div>
        <Select
          value={role}
          onChange={setRole}
          options={[{ value: '', label: 'All roles' }, ...ROLES.map((r) => ({ value: r, label: r }))]}
        />
        {can('users.invite') && (
          <Link to="/admin/invite"><Button size="sm" icon={<UserPlus size={13} />}>Add someone</Button></Link>
        )}
      </div>

      {users.isLoading && <div className="text-sm text-muted">Loading...</div>}
      {users.data?.length === 0 && <Card><p className="text-sm text-muted">Nobody matches that.</p></Card>}

      <div className="space-y-2">
        {users.data?.map((u) => (
          <Card key={u.id} className={cn('py-3', !u.active && 'opacity-60')}>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-muted-strong truncate">{u.display_name}</span>
                  <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-mono', ROLE_TONE[u.role])}>
                    {u.role}
                  </span>
                  {!u.active && <span className="text-[10px] text-red-400 font-mono">DEACTIVATED</span>}
                  {!u.last_seen_at && u.active && u.created_at.slice(0, 10) >= SEEN_TRACKED_SINCE && (
                    <span className="text-[10px] text-amber-400 font-mono">NEVER SIGNED IN</span>
                  )}
                </div>
                <div className="text-xs text-muted truncate">{u.email}</div>
                {/* what this person can SEE; permissions below are what they can DO */}
                {!u.unrestricted && u.role !== 'client' && (
                  <div className="text-[11px] mt-1 flex gap-1.5 flex-wrap items-center">
                    <span className={cn('font-mono px-1.5 py-0.5 rounded border',
                      u.access_level === 'clients'
                        ? 'text-sky-300 border-sky-500/30 bg-sky-500/10'
                        : 'text-amber-300 border-amber-500/30 bg-amber-500/10')}>
                      {u.access_level === 'clients' ? 'clinic-wide' : 'own tasks only'}
                    </span>
                    {u.default_crew && (
                      <span className="font-mono px-1.5 py-0.5 rounded border text-emerald-300 border-emerald-500/30 bg-emerald-500/10">default crew</span>
                    )}
                    {u.client_ids.length === 0
                      ? <span className="text-red-400 font-mono">no clinics: sees nothing</span>
                      : u.client_ids.map((id) => (
                          <span key={id} className="px-1.5 py-0.5 rounded bg-slate-800 text-muted-strong">{clinicName(id)}</span>
                        ))}
                  </div>
                )}
                {(u.extra_scopes.length > 0 || u.denied_scopes.length > 0) && (
                  <div className="text-[11px] mt-1 flex gap-2 flex-wrap">
                    {u.extra_scopes.map((s) => (
                      <span key={s} className="text-emerald-400 font-mono">+{s}</span>
                    ))}
                    {u.denied_scopes.map((s) => (
                      <span key={s} className="text-red-400 font-mono">-{s}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* flex-wrap: at phone widths the three controls otherwise run 5px past
                  the viewport and the whole page scrolls sideways. */}
              <div className="flex items-center gap-2 flex-wrap">
                {can('roles.assign') && (
                  <Select
                    value={u.role}
                    onChange={(v) => roleMut.mutate({ id: u.id, r: v })}
                    disabled={roleMut.isPending}
                    options={ROLES.map((r) => ({ value: r, label: r }))}
                  />
                )}
                {can('users.scope') && !u.unrestricted && u.role !== 'client' && (
                  <Button size="sm" variant="secondary" icon={<Building2 size={12} />} onClick={() => setAccess(u)}>
                    Clinics
                  </Button>
                )}
                {can('scopes.grant') && (
                  <Button size="sm" variant="secondary" icon={<KeyRound size={12} />} onClick={() => setEditing(u)}>
                    Permissions
                  </Button>
                )}
                {can('users.impersonate') && u.active && u.role !== 'owner' && u.id !== meUser?.id && (
                  <Button size="sm" variant="secondary" icon={<Eye size={12} />} onClick={() => viewAs.mutate(u)} disabled={viewAs.isPending}>
                    View as
                  </Button>
                )}
                {can('users.deactivate') && (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={u.active ? <UserX size={12} /> : <UserCheck size={12} />}
                    onClick={() => setConfirm({ u, next: !u.active })}
                    disabled={activeMut.isPending}
                  >
                    {u.active ? 'Deactivate' : 'Reactivate'}
                  </Button>
                )}
                {can('users.deactivate') && !u.active && u.role !== 'owner' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 size={12} />}
                    onClick={() => setRemoving(u)}
                    disabled={removeMut.isPending}
                    title="Delete the account for good. Only possible while it has no history."
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {editing && <ScopeEditor user={editing} onClose={() => { setEditing(null); invalidate(); }} />}
      {access && <AccessEditor user={access} onClose={() => { setAccess(null); invalidate(); }} />}

      {confirm && (
        <ConfirmDialog
          open
          title={confirm.next ? 'Reactivate this person?' : 'Deactivate this person?'}
          message={
            confirm.next
              ? `${confirm.u.display_name} will be able to sign in again.`
              : `${confirm.u.display_name} will be signed out and cannot sign in. Their work and history stay intact.`
          }
          confirmLabel={confirm.next ? 'Reactivate' : 'Deactivate'}
          onConfirm={() => { activeMut.mutate({ id: confirm.u.id, a: confirm.next }); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}

      {removing && (
        <ConfirmDialog
          open
          title={`Delete ${removing.display_name} for good?`}
          message={'This cannot be undone. It only goes through while the account has no history: once they have held a task, written a comment or decided an approval, the app refuses and tells you what holds them, and deactivating is the right move instead.'}
          confirmLabel="Delete for good"
          onConfirm={() => { removeMut.mutate(removing); setRemoving(null); }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </>
  );
}

/** Which clinics a person sees, and how much of them. This is visibility, kept
 *  deliberately apart from permissions: an admin decides what someone can SEE
 *  here and what they can DO in the permissions editor. */
function AccessEditor({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const clinics = useQuery({ queryKey: ['clients'], queryFn: fetchClients });
  const [ids, setIds] = useState<string[]>(user.client_ids);
  const [level, setLevel] = useState<'clients' | 'tasks'>(user.access_level);
  const [crew, setCrew] = useState<boolean>(user.default_crew);
  const save = useMutation({
    mutationFn: () => setUserAccess(user.id, ids, level, crew),
    onSuccess: () => { toast.success('Clinic access updated'); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Could not save clinic access'),
  });
  const toggle = (id: string) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-bg-card border border-slate-800 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div>
            <h3 className="font-medium text-muted-strong">Clinics</h3>
            <p className="text-xs text-muted">{user.display_name} &middot; what they can see</p>
          </div>
          <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] text-muted uppercase tracking-wider mb-2">Clinics this person works on</div>
            {clinics.data?.length === 0 && <p className="text-xs text-muted">No clients exist yet.</p>}
            <div className="space-y-1">
              {clinics.data?.map((c) => (
                <label key={c.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-800/50 cursor-pointer">
                  <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} />
                  <span className="text-sm text-muted-strong">{c.name}</span>
                </label>
              ))}
            </div>
            {ids.length === 0 && (
              <p className="text-[11px] text-red-400 mt-2">With no clinics ticked this person sees nothing at all.</p>
            )}
          </div>

          <div>
            <div className="text-[10px] text-muted uppercase tracking-wider mb-2">How much of those clinics</div>
            <label className="flex items-start gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-800/50 cursor-pointer">
              <input type="radio" name="lvl" checked={level === 'clients'} onChange={() => setLevel('clients')} className="mt-1" />
              <span>
                <span className="text-sm text-muted-strong">Clinic-wide</span>
                <span className="block text-[11px] text-muted">Every creative, strategy, task and request of those clinics. For art directors and content creators.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-800/50 cursor-pointer">
              <input type="radio" name="lvl" checked={level === 'tasks'} onChange={() => setLevel('tasks')} className="mt-1" />
              <span>
                <span className="text-sm text-muted-strong">Own tasks only</span>
                <span className="block text-[11px] text-muted">Only creatives they are assigned to, inside those clinics. For videographers and editors.</span>
              </span>
            </label>
          </div>

          <div>
            <div className="text-[10px] text-muted uppercase tracking-wider mb-2">New clinics</div>
            <label className="flex items-start gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-800/50 cursor-pointer">
              <input type="checkbox" name="crew" checked={crew} onChange={(e) => setCrew(e.target.checked)} className="mt-1" />
              <span>
                <span className="text-sm text-muted-strong">On every new clinic by default</span>
                <span className="block text-[11px] text-muted">Added automatically when a client is created, at the level above. Not retroactive.</span>
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-800">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving...' : 'Save clinic access'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Grant beyond the role, or revoke from within it. The role default is shown
 *  next to every scope so it is obvious what is inherited and what is an
 *  exception someone deliberately made. */
function ScopeEditor({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const cat = useQuery({ queryKey: ['scope-catalogue'], queryFn: fetchScopeCatalogue });
  const [extra, setExtra] = useState<string[]>(user.extra_scopes);
  const [denied, setDenied] = useState<string[]>(user.denied_scopes);

  const save = useMutation({
    mutationFn: () => setUserScopes(user.id, extra, denied),
    onSuccess: () => { toast.success('Permissions updated'); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Could not save permissions'),
  });

  const roleHas = new Set(cat.data?.role_defaults[user.role] ?? []);

  function toggle(key: string) {
    const inherited = roleHas.has(key);
    const granted = extra.includes(key);
    const revoked = denied.includes(key);
    if (inherited) {
      // Inherited scopes cycle between kept and revoked.
      setDenied(revoked ? denied.filter((s) => s !== key) : [...denied, key]);
    } else {
      // Non-inherited cycle between absent and granted.
      setExtra(granted ? extra.filter((s) => s !== key) : [...extra, key]);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-bg-card border border-slate-800 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div>
            <h3 className="font-medium text-muted-strong">Permissions</h3>
            <p className="text-xs text-muted">{user.display_name} &middot; role <span className="font-mono">{user.role}</span></p>
          </div>
          <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
        </div>

        <div className="p-5 space-y-1.5">
          <p className="text-xs text-muted mb-3">
            Ticked comes from the role. Untick to revoke it from this person, or tick something
            the role does not include to grant it. Permissions control what someone can DO;
            they never change which clients they can see.
          </p>
          {Array.from(new Map((cat.data?.scopes ?? []).map((s) => [s.group ?? 'Other', true])).keys()).map((g) => (
            <div key={g} className="pt-3 first:pt-0">
              <div className="text-[10px] uppercase tracking-wide text-muted mb-1 px-2" data-testid="scope-group">{g}</div>
          {(cat.data?.scopes ?? []).filter((s) => (s.group ?? 'Other') === g).map((s) => {
            const inherited = roleHas.has(s.key);
            const revoked = denied.includes(s.key);
            const granted = extra.includes(s.key);
            const on = (inherited && !revoked) || granted;
            return (
              <label
                key={s.key}
                className="flex items-start gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-800/50 cursor-pointer"
              >
                <input type="checkbox" checked={on} onChange={() => toggle(s.key)} className="mt-0.5" />
                <span className="flex-1 min-w-0" data-testid={`scope-${s.key}`}>
                  {s.label && <span className="block text-sm text-muted-strong">{s.label}</span>}
                  <span className="font-mono text-xs text-muted-strong">{s.key}</span>
                  {inherited && !revoked && <span className="text-[10px] text-muted ml-2">from role</span>}
                  {granted && <span className="text-[10px] text-emerald-400 ml-2">granted</span>}
                  {revoked && <span className="text-[10px] text-red-400 ml-2">revoked</span>}
                  <span className="block text-[11px] text-muted">{s.hint || s.description}</span>
                </span>
              </label>
            );
          })}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-800">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving...' : 'Save permissions'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Work
// ---------------------------------------------------------------------------

function WorkTab({ can }: { can: (s: string) => boolean }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'overdue' | 'unassigned' | 'blocked'>('all');

  const params =
    filter === 'overdue' ? { overdue: true }
    : filter === 'unassigned' ? { unassigned: true }
    : filter === 'blocked' ? { status: 'blocked' }
    : {};

  const tasks = useQuery({ queryKey: ['admin-tasks', filter], queryFn: () => fetchAdminTasks(params) });
  const people = useQuery({ queryKey: ['admin-users', '', ''], queryFn: () => fetchAdminUsers({ active: true }) });

  const reassign = useMutation({
    mutationFn: ({ id, to }: { id: string; to: string | null }) => reassignTask(id, to),
    onSuccess: () => { toast.success('Task reassigned'); qc.invalidateQueries({ queryKey: ['admin-tasks'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Could not reassign'),
  });

  // A client can never hold production work, so never offer them.
  const assignable = (people.data ?? []).filter((p) => p.role !== 'client');

  return (
    <>
      <div className="flex gap-1 mb-4 flex-wrap">
        {(['all', 'overdue', 'unassigned', 'blocked'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'text-xs px-2.5 py-1 rounded-full border capitalize',
              filter === f
                ? 'border-accent-400 text-accent-400 bg-accent-400/10'
                : 'border-slate-700 text-muted hover:text-muted-strong',
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {tasks.isLoading && <div className="text-sm text-muted">Loading...</div>}
      {tasks.data?.length === 0 && (
        <Card><p className="text-sm text-muted">Nothing here. That is usually good.</p></Card>
      )}

      <div className="space-y-2">
        {tasks.data?.map((t: AdminTask) => (
          <Card key={t.id} className="py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[11px] text-accent-400">{t.creative_code}</span>
                  <span className="text-sm text-muted-strong truncate">{t.creative_title}</span>
                </div>
                <div className="text-xs text-muted">
                  <span className="font-mono">{t.stage}</span> &middot; {t.status}
                  {t.due_date && <> &middot; due {t.due_date}</>}
                  {!t.assignee_id && <span className="text-amber-400"> &middot; unassigned</span>}
                </div>
              </div>
              {can('tasks.assign') && (
                <Select
                  value={t.assignee_id ?? ''}
                  onChange={(v) => reassign.mutate({ id: t.id, to: v || null })}
                  disabled={reassign.isPending}
                  options={[
                    { value: '', label: 'Unassigned' },
                    ...assignable.map((p) => ({ value: p.id, label: p.display_name, hint: p.role })),
                  ]}
                />
              )}
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Roles and permissions reference
// ---------------------------------------------------------------------------

function ScopesTab() {
  const cat = useQuery({ queryKey: ['scope-catalogue'], queryFn: fetchScopeCatalogue });
  if (!cat.data) return <div className="text-sm text-muted">Loading...</div>;

  return (
    <>
      <Card className="mb-4">
        <div className="flex items-start gap-3">
          <AlertTriangle size={16} className="text-accent-400 shrink-0 mt-0.5" />
          <p className="text-xs text-muted">
            Permissions control what someone can <strong className="text-muted-strong">do</strong>.
            They never control which clients someone can <strong className="text-muted-strong">see</strong>.
            Client separation is enforced separately and cannot be granted away here.
            {' '}<strong className="text-muted-strong">Own-tasks-only people</strong> (videographers, editors, designers) additionally lose
            the planning permissions whatever their role says: no briefs, strategies, stage moves, approvals or assigning.
            They upload, submit their task for review, comment and mark up. Switch someone to clinic-wide under People, Clinics to lift that.
          </p>
        </div>
      </Card>

      {Object.keys(cat.data.designation_defaults ?? {}).length > 0 && (
        <Card className="mb-4">
          <h3 className="text-sm font-medium text-muted-strong mb-1">The job title adds to the role</h3>
          <p className="text-xs text-muted mb-2">
            A permission that belongs to a job rather than a rank comes from the designation, so
            giving someone the title gives them the button. A revoke under Permissions still wins.
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(cat.data.designation_defaults ?? {}).map(([d, list]) => (
              <span key={d} className="text-[11px] px-2 py-1 rounded-lg border border-slate-800 text-muted">
                <span className="text-muted-strong">{d.replace(/_/g, ' ')}</span>
                {' '}&rarr; <span className="font-mono text-accent-400">{list.join(', ')}</span>
              </span>
            ))}
          </div>
        </Card>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left p-2 text-[10px] uppercase tracking-wider text-muted border-b border-slate-700">Permission</th>
              {ROLES.map((r) => (
                <th key={r} className="p-2 text-[10px] uppercase tracking-wider text-muted border-b border-slate-700">{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cat.data.scopes.map((s) => (
              <tr key={s.key} className="border-b border-slate-800">
                <td className="p-2">
                  {s.label && <div className="text-sm text-muted-strong">{s.label}</div>}
                  <div className="font-mono text-xs text-muted-strong">{s.key}</div>
                  <div className="text-[11px] text-muted">{s.description}</div>
                </td>
                {ROLES.map((r) => (
                  <td key={r} className="p-2 text-center">
                    {cat.data!.role_defaults[r]?.includes(s.key)
                      ? <span className="text-emerald-400">&#10003;</span>
                      : <span className="text-slate-700">&middot;</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

function AuditTab() {
  const q = useQuery({ queryKey: ['admin-audit'], queryFn: () => fetchAudit(100) });
  if (q.isLoading) return <div className="text-sm text-muted">Loading...</div>;
  if (!q.data?.length) return <Card><p className="text-sm text-muted">No admin actions recorded yet.</p></Card>;

  return (
    <div className="space-y-2">
      {q.data.map((a) => (
        <Card key={a.id} className="py-2.5">
          <div className="text-xs">
            <span className="text-accent-400">{a.actor_name ?? 'system'}</span>{' '}
            <span className="text-muted">{a.verb.replace(/_/g, ' ')}</span>{' '}
            <span className="text-muted-strong">{a.target_title}</span>
          </div>
          <div className="text-[10px] text-muted mt-0.5">
            {new Date(a.at).toLocaleString('en-AE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Dubai' })}
          </div>
          {a.meta && Object.keys(a.meta).length > 0 && (
            <div className="text-[10px] font-mono text-muted mt-1">
              {Object.entries(a.meta).map(([k, v]) => (
                <div key={k}>
                  {k}: {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}


// Settings
// ---------------------------------------------------------------------------
const CLOCK_ROWS: { key: keyof StageSla; label: string; hint: string }[] = [
  { key: 'brief', label: 'Brief', hint: 'brief written and submitted' },
  { key: 'shoot', label: 'Shoot to raws uploaded', hint: 'from the shoot task until raws are submitted for AD review' },
  { key: 'raws_review', label: 'AD reviews raws', hint: 'art director filters and approves the raws' },
  { key: 'edit', label: 'Edit: rough cut, colouring', hint: 'from the edit task until the colouring is approved' },
  { key: 'dual_review', label: 'Final edit approval', hint: 'the content creator approves the final edit' },
  { key: 'client_review', label: 'Client review', hint: 'client approves or asks for an adjustment' },
];

function SettingsTab() {
  return (
    <Card>
      <div className="flex items-start gap-3">
        <Timer size={16} className="text-accent-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-medium text-muted-strong">Timing lives in Services SLA</h2>
          <p className="text-xs text-muted mt-1 max-w-2xl">
            Hours per handover for video and stills, the rejection extension, the auto-approval rules, the reminder window and
            KAF's own review clocks are all on one page now. Every countdown in the app comes from there.
          </p>
          <Link to="/sla" className="inline-block mt-3"><Button size="sm">Open Services SLA</Button></Link>
        </div>
      </div>
      <p className="text-[11px] text-muted mt-4">Not yet: locking a late creative, escalation on a silent client, working-hours calendars. Those wait on the six Execution Clock decisions.</p>
    </Card>
  );
}

